import React, { useCallback, useMemo } from 'react'
import { SizableText, YStack } from '@mythical/ui'
import { ScrollingContentCarousel as ScrollingContentCarouselType } from 'app/blocks/landing/scrolling-content-carousel'
import ContentCard, {
  ContentCardProps,
} from 'app/components/content/ContentCard'
import { Marquee } from 'app/components/primitive/Marquee'
import { Container } from 'app/components/structure/Container'
import { mapToContentCard } from 'app/queries/content/all-content-list'
import { MarketingHeading } from 'app/features/landing/components'
import { ButtonLink } from 'app/components/primitive/Button'
import { useStores } from 'app/store'
import { SolitoImage } from 'solito/image'

interface ScrollingContentCarouselBlockProps {
  block: ScrollingContentCarouselType
}

export const ScrollingContentCarouselBlock: React.FC<
  ScrollingContentCarouselBlockProps
> = ({ block }) => {
  const { content = [], title, subtitle } = block
  const formattedContent = content.map((item) => mapToContentCard(item, {}))
  const { userStore } = useStores()
  const canMemberfulLink = userStore?.canMemberfulLink ?? false

  const row1 = useMemo(
    () => formattedContent.slice(0, Math.ceil(formattedContent.length / 2)),
    [content],
  )

  const row2 = useMemo(
    () => formattedContent.slice(Math.ceil(formattedContent.length / 2)),
    [content],
  )

  const renderItem = useCallback((item) => {
    return (
      <YStack w={228} $gtMd={{ w: 456 }}>
        <ContentCard
          {...item}
          image={item.image2x}
          sizes="(min-width: 1020px) 456px, 230px"
        />
      </YStack>
    )
  }, [])

  if (content.length === 0) return null

  return (
    <YStack pos="relative">
      <Container variant="ultraWide" gap="$5" $gtSm={{ gap: '$8.5' }}>
        <Container
          variant="mediumLarge"
          jc="center"
          ai="center"
          gap="$3.5"
          $gtSm={{ gap: '$5' }}
          paddedX
        >
          <MarketingHeading tag="h2" ta="center" mx="auto">
            {title}
          </MarketingHeading>
          <SizableText
            ff="$title"
            ta="center"
            size="$m"
            $gtSm={{ size: '$xl' }}
          >
            {subtitle}
          </SizableText>
          {canMemberfulLink && block.ctaLabel && (
            <ButtonLink href={block.ctaLink || '/perks'}>
              {block.ctaLabel}
            </ButtonLink>
          )}
        </Container>
        <YStack gap="$3.5" overflow="hidden">
          <Marquee
            items={row1}
            spacing={12}
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
          />
          <Marquee
            items={row2}
            spacing={12}
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
            scrollDirection="backward"
            emblaOptions={{
              align: 'center',
            }}
          />
        </YStack>
      </Container>
      <YStack
        pos="absolute"
        w={120}
        h={120}
        bottom="-5%"
        left="5%"
        $gtMd={{ width: 159, height: 159 }}
      >
        <SolitoImage
          alt="Become a mythical beast"
          fill
          src={require('app/assets/svg/members-only-sticker.svg')}
        />
      </YStack>
    </YStack>
  )
}
